import React, {useState} from 'react';
import {Redirect} from '@reach/router';
import {compose, graphql} from 'react-apollo';
import {useQuery} from 'react-apollo-hooks';
import gql from 'graphql-tag';
import {useFormState} from 'react-use-form-state';

import {Card, Loading, Input, Button} from 'ui-kit';

import * as styles from './AccountInvitation.module.css';

function AccountInvitation({mutate}) {
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationErrors, setMutationErrors] = useState({});
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [formState, {password}] = useFormState({
    password: '',
    confirmPassword: '',
  });

  const {p, i, t} = window.location.search
    .slice(1)
    .split('&')
    .reduce((params, currentParam) => {
      const [key, value] = currentParam.split('=');

      return {
        ...params,
        [key]: value,
      };
    }, {});

  const {data, error, loading} = useQuery(
    gql`
      query AccountInvitation($domain: String!) {
        practice(domain: $domain) {
          name
        }
      }
    `,
    {
      variables: {
        domain: p,
      },
    },
  );

  async function handleSubmit(event) {
    event.preventDefault();

    setMutationLoading(true);
    setMutationErrors({});

    try {
      const {
        data: {completeStaffMemberAccountInvitation},
      } = await mutate({
        variables: {
          input: {
            id: i,
            token: t,
            password: formState.values.password,
            passwordConfirmation: formState.values.confirmPassword,
          },
        },
      });

      const {errors} = completeStaffMemberAccountInvitation;

      if (errors.length > 0) {
        const clientErrors = errors.reduce((allErrors, {path, message}) => {
          if (allErrors[path] == null) {
            return {
              ...allErrors,
              [path]: message,
            };
          }

          return {
            ...allErrors,
            [path]: `${allErrors[path]}; ${message}`,
          };
        }, {});

        setMutationErrors(clientErrors);
        setMutationLoading(false);
      } else {
        setShouldRedirect(true);
        setMutationLoading(false);
      }
    } catch (error) {
      console.error(error);
      setMutationLoading(false);
    }
  }

  if (shouldRedirect) {
    return <Redirect noThrow to="/" />;
  }

  if (loading) {
    return (
      <div className={styles.Container}>
        <Loading />
      </div>
    );
  }

  if (error != null) {
    console.error(error);
    return (
      <div className={styles.Container}>
        <p>An unexpected error occurred.</p>
      </div>
    );
  }

  const {practice} = data;

  return (
    <div className={styles.Container}>
      <Card>
        <h3>Set up your account</h3>

        <p>
          <small>
            You've been invited to join the Plum workspace for {practice.name}.
            Enter your password below to complete your account setup.
          </small>
        </p>

        <form method="post" onSubmit={handleSubmit}>
          <Input
            label="Password"
            errorMessage={mutationErrors.password}
            {...password('password')}
          />
          <Input
            label="Confirm password"
            errorMessage={mutationErrors.passwordConfirmation}
            {...password('confirmPassword')}
          />

          <Button block>
            {mutationLoading ? <Loading immediate /> : 'Submit'}
          </Button>

          {mutationErrors.global == null &&
          mutationErrors.token == null ? null : (
            <p className={styles.GlobalError}>
              <small>Your request is invalid.</small>
            </p>
          )}
        </form>
      </Card>
    </div>
  );
}

export default compose(
  graphql(
    gql`
      mutation CompleteStaffMemberAccountInvitation(
        $input: CompleteStaffMemberAccountInvitationInput!
      ) {
        completeStaffMemberAccountInvitation(input: $input) {
          errors {
            path
            message
          }
        }
      }
    `,
  ),
)(AccountInvitation);
